import React, { useEffect, useMemo, useState } from 'react';
import advisor from '../../lib/advisor';
import ProvisionalRecommendationCard from './ProvisionalRecommendationCard';
import UnknownVisitorInvite from './UnknownVisitorInvite';

/**
 * AdvisorSearchLens — la voz del Advisor dentro de /buscar.
 *
 * Este componente NO es un panel lateral. Es la lente narrativa que se
 * inyecta en la página cuando existe suficiente contexto del usuario.
 *
 * Reglas de implementación:
 *  · R1 · Encabezado nunca vacío — sólo se renderiza cuando puede citar
 *         algo concreto del ConfidenceGraph.
 *  · R2 · La Recomendación Provisional se ve especial, no primera.
 *  · R3 · La explicación proviene del ExplainabilityEngine (backend) — este
 *         componente NO fabrica explicaciones genéricas.
 *  · R4 · Cuando la top-pick cambia, el mensaje transmite evolución, no error.
 *  · R5 · Sin contexto suficiente, se muestra un banner discreto (P3).
 *  · Regla del silencio · Sin cosas concretas que decir, se calla.
 *
 * Props:
 *   - onRecommendationsResolved(items[]) — callback opcional para pasar
 *     al padre el mapa itemId → advisorReason, de modo que la grilla
 *     pueda decorar cada card con su explicación inline.
 */

const LS_LAST_TOP_KEY = 'habita.advisor.lastTopPick';

// Umbrales de firmeza (aprobados en Fase 1)
function computeFirmness({ fundamentalsResolved, viewed, saved, dismissed }) {
  if (fundamentalsResolved >= 4 && saved >= 2 && dismissed >= 2) {
    return { level: 'recomendacion_provisional', label: 'Recomendación provisional', confidence: 65 };
  }
  if (fundamentalsResolved >= 3 && viewed >= 3) {
    return { level: 'sugerencia_refinada', label: 'Sugerencia refinada', confidence: 45 };
  }
  if (fundamentalsResolved >= 2) {
    return { level: 'sugerencia_inicial', label: 'Sugerencia inicial', confidence: 25 };
  }
  return null; // no hay firmeza aún — no mostrar
}

// Extrae lo conocido del ConfidenceGraph — devuelve solo lo que tiene valor.
function extractKnown(graph) {
  const attrs = graph?.attributes || {};
  const get = (k) => (attrs[k]?.value != null ? attrs[k].value : null);
  return {
    operation: get('operation'),
    city: get('city'),
    property_type: get('property_type'),
    neighborhood: get('neighborhood'),
    budget_min: get('budget_min'),
    budget_max: get('budget_max'),
    beds: get('beds'),
  };
}

// Cuenta cuántos fundamentales están resueltos.
function countFundamentals(known) {
  return ['operation', 'city', 'property_type', 'neighborhood']
    .filter((k) => known[k] != null && known[k] !== '')
    .length;
}

// Formatea un rango de precio en pesos colombianos.
function _formatCOP(v) {
  if (v == null || Number.isNaN(v)) return null;
  // Nomenclatura colombiana: siempre en millones ('M'), nunca 'B'.
  if (v >= 1_000_000) return `$${Math.round(v / 1_000_000).toLocaleString('es-CO')}M`;
  return `$${Number(v).toLocaleString('es-CO')}`;
}

/**
 * Construye el encabezado narrado citando fundamentales concretos.
 * Devuelve null si no hay al menos 2 fundamentales que citar (R1).
 */
function narrateHeader(known, total) {
  const parts = [];
  if (known.property_type) {
    const t = String(known.property_type).toLowerCase();
    parts.push(known.operation === 'venta' ? `${t} en venta` : known.operation === 'arriendo' ? `${t} en arriendo` : t);
  } else if (known.operation === 'venta') {
    parts.push('propiedades en venta');
  } else if (known.operation === 'arriendo') {
    parts.push('propiedades en arriendo');
  }
  if (known.city) parts.push(`en ${known.city}`);
  if (known.neighborhood) parts.push(`con foco en ${known.neighborhood}`);

  const range = [];
  if (known.budget_min) range.push(_formatCOP(known.budget_min));
  if (known.budget_max) range.push(_formatCOP(known.budget_max));
  const priceStr = range.length ? `, presupuesto ${range.join('–')}` : '';

  if (parts.length < 2) return null; // nada suficientemente concreto que citar

  const summary = parts.join(' ') + priceStr;
  return {
    lead: `Hasta ahora entendí que buscas ${summary}.`,
    tail: `Con ese contexto encontré ${total || 'estas'} propiedades para ti — ordenadas por qué tan bien encajan.`,
  };
}

/**
 * Compone el mensaje de "evolución" cuando cambia el top-pick.
 * Nunca dice "estaba equivocado". Siempre framing de aprendizaje (R4).
 */
function evolutionMessageFor(prevPick, newExplanation) {
  if (!prevPick) return null;
  const base =
    'Después de conocer mejor tus preferencias, ahora considero que esta opción representa una mejor alternativa.';
  if (!newExplanation) return base;
  return base;
}

export default function AdvisorSearchLens({
  totalItems,
  onRecommendationsResolved,
}) {
  const [known, setKnown] = useState(null);        // atributos del Confidence Graph
  const [items, setItems] = useState(null);        // RecommendationItem[]
  const [firmness, setFirmness] = useState(null);  // {level,label,confidence}
  const [evolutionMsg, setEvolutionMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. Contexto actual
      const graph = await advisor.getConfidenceGraph();
      if (cancelled) return;
      const knownAttrs = graph ? extractKnown(graph) : null;
      const fundamentalsResolved = knownAttrs ? countFundamentals(knownAttrs) : 0;

      // Regla del silencio: sin fundamentales, no rendereamos voz del Advisor.
      if (fundamentalsResolved < 2) {
        setKnown(knownAttrs || {});
        setItems([]);
        setFirmness(null);
        setLoading(false);
        return;
      }

      // 2. Recomendaciones ordenadas por el RecommendationEngine
      // El endpoint aceita hasta limit=6. Pedimos el máximo para poder decorar
      // varias cards inline con explicaciones específicas (R3).
      let recItems = [];
      try {
        const rec = await advisor.recommend({ limit: 6 });
        if (rec?.items && Array.isArray(rec.items)) recItems = rec.items;
      } catch { /* si falla, seguimos sin lens */ }
      if (cancelled) return;

      // 3. Firmeza — depende de fundamentales + estadísticas del grafo
      const stats = graph?.stats || {};
      const level = computeFirmness({
        fundamentalsResolved,
        viewed: stats.viewed_count || 0,
        saved: stats.saved_count || 0,
        dismissed: stats.dismissed_count || 0,
      });

      // 4. Evolución del top pick — si cambió respecto a la sesión anterior (R4)
      let evolution = null;
      try {
        const prevRaw = window.localStorage.getItem(LS_LAST_TOP_KEY);
        const prev = prevRaw ? JSON.parse(prevRaw) : null;
        const newTop = recItems[0];
        if (newTop && prev && prev.item_id && prev.item_id !== newTop.item_id) {
          evolution = evolutionMessageFor(prev, newTop.explanation);
        }
        if (newTop?.item_id) {
          window.localStorage.setItem(
            LS_LAST_TOP_KEY,
            JSON.stringify({ item_id: newTop.item_id, at: Date.now() }),
          );
        }
      } catch { /* localStorage puede fallar en modo privado */ }

      setKnown(knownAttrs);
      setItems(recItems);
      setFirmness(level);
      setEvolutionMsg(evolution);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Cuando cambian los items, pasamos al padre el mapa itemId → advisorReason
  // para que la grilla pueda mostrar la explicación inline en cada card (R3).
  useEffect(() => {
    if (!onRecommendationsResolved) return;
    const map = {};
    (items || []).forEach((it) => {
      const reason = it.explanation?.human_readable;
      if (reason && it.item_id) map[it.item_id] = reason;
    });
    onRecommendationsResolved(map);
  }, [items, onRecommendationsResolved]);

  // Encabezado narrado (memoizado)
  const narration = useMemo(() => {
    if (!known) return null;
    return narrateHeader(known, totalItems);
  }, [known, totalItems]);

  if (loading) return null;

  // Sin fundamentales suficientes → invitación discreta (R5, P3)
  if (!firmness || !narration) {
    return (
      <div className="mb-5" data-testid="advisor-search-lens-empty">
        <UnknownVisitorInvite />
      </div>
    );
  }

  const topPick = (items || [])[0] || null;

  return (
    <div className="mb-6" data-testid="advisor-search-lens">
      {/* Encabezado narrado — cita fundamentales concretos (R1, P2, P5) */}
      <header className="mb-5" data-testid="advisor-search-header">
        <p className="text-[11px] uppercase tracking-widest text-habita font-semibold mb-2">
          Habita Advisor
        </p>
        <p className="text-base sm:text-lg text-habita-dark leading-relaxed">
          {narration.lead}
          <br />
          <span className="text-sm text-gray-600 italic">{narration.tail}</span>
        </p>
      </header>

      {/* La propiedad que hoy es la mejor opción (R2) */}
      {topPick && (
        <ProvisionalRecommendationCard
          item={topPick}
          firmnessLabel={firmness.label}
          confidencePct={firmness.confidence}
          evolutionMessage={evolutionMsg}
        />
      )}
    </div>
  );
}
